import {
  applyDraftAnswer,
  countRequiredBlankFields,
  invalidateDraftOnStepEdit,
} from '../../domain/draftInvalidation.js';
import { validateOperatingSchedule } from '../../domain/schedule.js';
import { applyAiFillPatches } from '../../domain/parse.js';
import { createEmptyWorldDraft } from '../../db/worldPersistence.js';
import { WIZARD_QUESTION_IDS } from '../../constants.js';
import type {
  AiFillPatch,
  AiFillResult,
  AnswerSource,
  AnswerStatus,
  CharacterDraft,
  CharacterDraftMeta,
  ConnectedAnswers,
  LocationGrounding,
  OperatingSchedule,
  RhythmAnswer,
  TimePeriod,
  WizardStepId,
  WorldDraft,
} from '../../domain/types.js';
import { CHARACTER_DRAFT_SCHEMA_VERSION } from '../../domain/types.js';
import { defaultSchedule, suggestTimePeriod } from './fallbacks.js';
import { applyLocalAiFillForStep } from './suggestionProvider.js';
import {
  emptyConnectedAnswers,
  emptyLocationGrounding,
} from './questionSchema.js';
import { scheduleWithLocationTimezone } from './scheduleTimezone.js';
import { uid } from '../spawn.js';

export function createFreshDraft(): WorldDraft {
  return createEmptyWorldDraft(uid('draft'));
}

export function touchDraft(draft: WorldDraft, patch: Partial<WorldDraft>): WorldDraft {
  return { ...draft, ...patch, updatedAt: new Date().toISOString() };
}

export function setWizardStep(draft: WorldDraft, step: WizardStepId): WorldDraft {
  return touchDraft(draft, { wizardStep: step });
}

function fieldStatusForValue(
  value: string,
  source: AnswerSource | null,
): AnswerStatus {
  if (!value.trim()) return 'unanswered';
  if (source === 'ai_suggestion' || source === 'fallback') return 'suggested';
  return 'answered';
}

function syncGroundingAnswer(
  draft: WorldDraft,
  questionId: string,
  value: string,
  status: AnswerStatus,
  source: AnswerSource | null,
  allowConfirmedOverwrite = false,
): WorldDraft {
  const result = applyDraftAnswer(draft, {
    questionId,
    value,
    status,
    source,
    allowConfirmedOverwrite,
  });
  return result.ok ? result.draft : draft;
}

/** Apply grounding aggregate; mirrors fields into answer records and stales downstream on change. */
export function setLocationGrounding(
  draft: WorldDraft,
  location: LocationGrounding,
  options?: { source?: AnswerSource | null; allowConfirmedOverwrite?: boolean },
): WorldDraft {
  const prev = draft.location;
  const source = options?.source ?? 'user';
  const allow = options?.allowConfirmedOverwrite ?? false;

  let next = touchDraft(draft, { location });
  next = syncGroundingAnswer(
    next,
    WIZARD_QUESTION_IDS.groundingName,
    location.name,
    fieldStatusForValue(location.name, source),
    location.name.trim() ? source : null,
    allow,
  );
  next = syncGroundingAnswer(
    next,
    WIZARD_QUESTION_IDS.groundingRegion,
    location.region,
    fieldStatusForValue(location.region, source),
    location.region.trim() ? source : null,
    allow,
  );
  next = syncGroundingAnswer(
    next,
    WIZARD_QUESTION_IDS.groundingLanguage,
    location.language,
    fieldStatusForValue(location.language, source),
    location.language.trim() ? source : null,
    allow,
  );

  const changed =
    !prev ||
    prev.name !== location.name ||
    prev.region !== location.region ||
    prev.language !== location.language;

  if (changed) {
    next = invalidateDraftOnStepEdit(next, 'location-grounding');
  }
  if (next.schedule?.timezoneStrategy === 'location') {
    next = setSchedule(next, next.schedule);
  }
  return next;
}

function rhythmAnswerValue(rhythm: RhythmAnswer): unknown {
  if (rhythm.skipped) return { skipped: true };
  return { skipped: false, type: rhythm.type, otherDescription: rhythm.otherDescription };
}

function rhythmStatus(rhythm: RhythmAnswer): AnswerStatus {
  if (rhythm.skipped) return 'skipped';
  if (rhythm.type === 'other' && !(rhythm.otherDescription?.trim())) return 'unanswered';
  return rhythm.type ? 'answered' : 'unanswered';
}

/** Apply connected aggregate; mirrors fields into answer records and stales downstream on change. */
export function setConnected(
  draft: WorldDraft,
  connected: ConnectedAnswers,
  options?: { source?: AnswerSource | null; allowConfirmedOverwrite?: boolean; syncOptional?: boolean },
): WorldDraft {
  const source = options?.source ?? 'user';
  const allow = options?.allowConfirmedOverwrite ?? false;
  const syncOptional = options?.syncOptional ?? true;
  const prev = draft.connected;

  let next = touchDraft(draft, { connected });

  const apply = (questionId: string, value: unknown, status: AnswerStatus): void => {
    const result = applyDraftAnswer(next, {
      questionId,
      value,
      status,
      source: status === 'unanswered' ? null : source,
      allowConfirmedOverwrite: allow,
    });
    if (result.ok) next = result.draft;
  };

  apply(
    WIZARD_QUESTION_IDS.connectedLocationType,
    connected.locationType,
    fieldStatusForValue(connected.locationType, source),
  );
  apply(
    WIZARD_QUESTION_IDS.connectedDescription,
    connected.description,
    fieldStatusForValue(connected.description, source),
  );
  if (syncOptional) {
    apply(
      WIZARD_QUESTION_IDS.connectedCrowd,
      connected.crowd,
      connected.crowd.type ? fieldStatusForValue('x', source) : 'unanswered',
    );
    apply(
      WIZARD_QUESTION_IDS.connectedRhythm,
      rhythmAnswerValue(connected.rhythm),
      rhythmStatus(connected.rhythm),
    );
  }

  const changed =
    !prev ||
    prev.locationType !== connected.locationType ||
    prev.description !== connected.description ||
    JSON.stringify(prev.crowd) !== JSON.stringify(connected.crowd) ||
    JSON.stringify(prev.rhythm) !== JSON.stringify(connected.rhythm);

  if (changed) {
    next = invalidateDraftOnStepEdit(next, 'connected');
  }
  return next;
}

export function skipConnectedRhythm(draft: WorldDraft): WorldDraft {
  const connected = draft.connected ?? emptyConnectedAnswers();
  return setConnected(draft, {
    ...connected,
    rhythm: { skipped: true },
  });
}

export function clearConnectedRhythmSkip(draft: WorldDraft): WorldDraft {
  const connected = draft.connected ?? emptyConnectedAnswers();
  if (!connected.rhythm.skipped) return draft;
  return setConnected(draft, {
    ...connected,
    rhythm: { skipped: false, type: 'steady_busy_all_day' },
  });
}

export function setTimePeriod(draft: WorldDraft, timePeriod: TimePeriod): WorldDraft {
  const prev = draft.timePeriod;
  const changed =
    !prev ||
    prev.present !== timePeriod.present ||
    prev.period !== timePeriod.period ||
    prev.rationale !== timePeriod.rationale;
  let next = touchDraft(draft, { timePeriod });
  if (changed) {
    next = invalidateDraftOnStepEdit(next, 'time-period');
  }
  return next;
}

/** Write DeepSeek-resolved timezone without re-running map-only sync. */
export function applyResolvedLocationTimezone(
  draft: WorldDraft,
  schedule: OperatingSchedule,
): WorldDraft {
  return setSchedule(draft, schedule, { skipMapSync: true });
}

/** Apply spelling-corrected region from async location resolution. */
export function applyCorrectedLocationRegion(draft: WorldDraft, region: string): WorldDraft {
  if (!draft.location) return draft;
  return setLocationGrounding(draft, { ...draft.location, region });
}

export function setSchedule(
  draft: WorldDraft,
  schedule: OperatingSchedule,
  options?: { skipMapSync?: boolean },
): WorldDraft {
  const synced = options?.skipMapSync
    ? schedule
    : scheduleWithLocationTimezone(schedule, draft.location?.region);
  const prev = draft.schedule;
  const changed =
    !prev ||
    prev.hasOperatingHours !== synced.hasOperatingHours ||
    prev.timezoneStrategy !== synced.timezoneStrategy ||
    prev.timezone !== synced.timezone ||
    JSON.stringify(prev.daysOpen) !== JSON.stringify(synced.daysOpen) ||
    prev.openTime !== synced.openTime ||
    prev.closeTime !== synced.closeTime ||
    prev.closedBehavior !== synced.closedBehavior;
  let next = touchDraft(draft, { schedule: synced });
  if (changed) {
    next = invalidateDraftOnStepEdit(next, 'operating-hours');
  }
  return next;
}

export function setReviewAccepted(draft: WorldDraft, accepted: boolean): WorldDraft {
  return touchDraft(draft, { reviewAccepted: accepted });
}

/** Fill eligible review blanks with local suggestions; clears review acceptance. */
export function applyReviewBlankFill(draft: WorldDraft): WorldDraft {
  let next = draft;
  for (const step of ['location-grounding', 'connected'] as const) {
    next = applyLocalAiFillForStep(next, step).draft;
  }
  const { blanks } = countRequiredBlankFields(next);
  if (blanks.some((b) => b.fieldId === WIZARD_QUESTION_IDS.periodPresent)) {
    next = setTimePeriod(next, suggestTimePeriod(next.draftId));
  }
  if (blanks.some((b) => b.fieldId === 'schedule')) {
    next = ensureScheduleForDraft(next);
    if (!next.schedule || !validateOperatingSchedule(next.schedule).valid) {
      next = setSchedule(next, defaultSchedule(null, next.draftId));
    }
  }
  return setReviewAccepted(next, false);
}

export function ensureScheduleForDraft(draft: WorldDraft): WorldDraft {
  if (draft.schedule) return draft;
  return touchDraft(draft, { schedule: defaultSchedule(null, draft.draftId) });
}

export function patchAnswer(
  draft: WorldDraft,
  input: Parameters<typeof applyDraftAnswer>[1],
): { ok: true; draft: WorldDraft } | { ok: false; error: string; draft: WorldDraft } {
  const result = applyDraftAnswer(draft, input);
  if (!result.ok) return result;
  return { ok: true, draft: result.draft };
}

export function applyAiFillToDraft(
  draft: WorldDraft,
  patches: AiFillPatch[],
): { draft: WorldDraft; result: AiFillResult } {
  const { draft: patched, result } = applyAiFillPatches(draft, patches);
  let next = patched;

  const grounding = { ...(next.location ?? emptyLocationGrounding()) };
  let groundingTouched = false;
  const connected = { ...(next.connected ?? emptyConnectedAnswers()) };
  let connectedTouched = false;

  for (const patch of result.applied) {
    if (patch.questionId === WIZARD_QUESTION_IDS.groundingName) {
      grounding.name = String(patch.value);
      groundingTouched = true;
    } else if (patch.questionId === WIZARD_QUESTION_IDS.groundingRegion) {
      grounding.region = String(patch.value);
      groundingTouched = true;
    } else if (patch.questionId === WIZARD_QUESTION_IDS.groundingLanguage) {
      grounding.language = String(patch.value);
      groundingTouched = true;
    } else if (patch.questionId === WIZARD_QUESTION_IDS.connectedLocationType) {
      connected.locationType = String(patch.value);
      connectedTouched = true;
    } else if (patch.questionId === WIZARD_QUESTION_IDS.connectedDescription) {
      connected.description = String(patch.value);
      connectedTouched = true;
    } else if (patch.questionId === WIZARD_QUESTION_IDS.connectedCrowd) {
      connected.crowd = patch.value as ConnectedAnswers['crowd'];
      connectedTouched = true;
    } else if (patch.questionId === WIZARD_QUESTION_IDS.connectedRhythm) {
      connected.rhythm = patch.value as RhythmAnswer;
      connectedTouched = true;
    }
  }

  if (groundingTouched) {
    next = setLocationGrounding(next, grounding, { source: 'ai_suggestion' });
  }
  if (connectedTouched) {
    next = setConnected(next, connected, { source: 'ai_suggestion' });
  }

  return { draft: next, result };
}

/** @deprecated v2 review assumptions block removed — shim until CreateWorldWizard drops import. */
export function acceptAssumptionRecord(draft: WorldDraft, _assumptionId: string): WorldDraft {
  return draft;
}

export function confirmAllAnswers(draft: WorldDraft): WorldDraft {
  const now = new Date().toISOString();
  const answers = Object.fromEntries(
    Object.entries(draft.answers).map(([id, a]) => {
      if (a.status === 'stale' || a.status === 'unanswered') return [id, a];
      return [id, { ...a, status: 'confirmed' as const, updatedAt: now }];
    }),
  );
  return touchDraft(draft, { answers });
}

type LegacyCharacterInput = Omit<
  CharacterDraftMeta,
  'draftCharacterId' | 'schemaVersion' | 'updatedAt'
>;

type PopulationCharacterDefaults = Partial<
  Pick<CharacterDraftMeta, 'species' | 'sex' | 'ageRange'>
>;

/** Read-side view: v2 draft + flat v1 fields for CharacterPopulationAdapter (never persist). */
export type PopulationCharacterView = CharacterDraft & {
  firstName: string;
  lastInitial?: string;
  species: string;
  sex: 'M' | 'F';
  ageRange: string;
};

export type PopulationDraftView = Omit<WorldDraft, 'characterDrafts'> & {
  characterDrafts: PopulationCharacterView[];
};

/** Project flat v1 fields at read boundary; body sidecar supplies species/sex/ageRange. */
export function projectCharacterForPopulation(
  character: CharacterDraft,
  defaults?: PopulationCharacterDefaults,
): PopulationCharacterView {
  return {
    ...character,
    firstName: character.basic.firstName,
    lastInitial: character.basic.lastInitial,
    species: character.species ?? defaults?.species ?? 'Human',
    sex: character.sex ?? defaults?.sex ?? 'M',
    ageRange: character.ageRange ?? defaults?.ageRange ?? 'Adult',
  };
}

/** Adapter-facing draft snapshot — must not be written back to storage. */
export function projectDraftForPopulation(
  draft: WorldDraft,
  bodies: Record<string, PopulationCharacterDefaults>,
): PopulationDraftView {
  return {
    ...draft,
    characterDrafts: draft.characterDrafts.map((c) =>
      projectCharacterForPopulation(c, bodies[c.draftCharacterId]),
    ),
  };
}

function characterFromLegacyInput(
  meta: LegacyCharacterInput,
  draftCharacterId: string,
  updatedAt: string,
): CharacterDraft {
  return {
    draftCharacterId,
    schemaVersion: CHARACTER_DRAFT_SCHEMA_VERSION,
    basic: { firstName: meta.firstName, lastInitial: meta.lastInitial },
    sex: meta.sex,
    species: meta.species,
    ageRange: meta.ageRange,
    appearance: null,
    nature: null,
    traits: [],
    status: meta.status,
    source: meta.source,
    dependencyIds: [],
    updatedAt,
  };
}

export function addCharacterDraft(
  draft: WorldDraft,
  meta: LegacyCharacterInput,
): WorldDraft {
  const active = draft.characterDrafts.filter((c) => c.status !== 'removed');
  if (active.length >= 10) return draft;
  const now = new Date().toISOString();
  const character = characterFromLegacyInput(meta, uid('char'), now);
  return touchDraft(draft, { characterDrafts: [...draft.characterDrafts, character] });
}

export function updateCharacterDraft(
  draft: WorldDraft,
  draftCharacterId: string,
  patch: Partial<Omit<CharacterDraftMeta, 'draftCharacterId' | 'schemaVersion'>>,
): WorldDraft {
  const now = new Date().toISOString();
  return touchDraft(draft, {
    characterDrafts: draft.characterDrafts.map((c) => {
      if (c.draftCharacterId !== draftCharacterId) return c;
      const basic =
        patch.firstName !== undefined || patch.lastInitial !== undefined
          ? {
              ...c.basic,
              ...(patch.firstName !== undefined ? { firstName: patch.firstName } : {}),
              ...(patch.lastInitial !== undefined ? { lastInitial: patch.lastInitial } : {}),
            }
          : c.basic;
      return {
        ...c,
        basic,
        ...(patch.status !== undefined ? { status: patch.status } : {}),
        ...(patch.source !== undefined ? { source: patch.source } : {}),
        updatedAt: now,
      };
    }),
  });
}

export function removeCharacterDraft(draft: WorldDraft, draftCharacterId: string): WorldDraft {
  return touchDraft(draft, {
    characterDrafts: draft.characterDrafts.map((c) =>
      c.draftCharacterId === draftCharacterId ? { ...c, status: 'removed' as const } : c,
    ),
  });
}

export function resetGroundingStep(draft: WorldDraft): WorldDraft {
  return setLocationGrounding(touchDraft(draft, { location: null }), emptyLocationGrounding());
}

export function resetConnectedStep(draft: WorldDraft): WorldDraft {
  return setConnected(touchDraft(draft, { connected: null }), emptyConnectedAnswers());
}

export function resetDraftCompletely(): WorldDraft {
  return createFreshDraft();
}

/** @deprecated v1 — location-type screen removed. */
export function setLocationType(
  draft: WorldDraft,
  _locationType: unknown,
): WorldDraft {
  return draft;
}

/** @deprecated v1 — mode screen removed. */
export function setCreationMode(draft: WorldDraft, _mode: unknown): WorldDraft {
  return draft;
}
